import { FC } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useModuleVisibility } from '../../hooks/useModuleVisibility';
import {
  ArrowLeft,
  UsersThree,
  ChartBar,
} from '@phosphor-icons/react';
import LeopartLoader from '../../shared/LeopartLoader/LeopartLoader';
import CustomButton from '../../shared/CustomButton/CustomButton';
import Typhography from '../../shared/Typhography/Typhography';
import { SessionCanvas } from './SessionCanvas';
import { RosterPanel } from './panels/RosterPanel';
import { AnalysisPanel } from './panels/AnalysisPanel';
import { RecoveryAlertsBanner } from './panels/RecoveryAlertsBanner';
import { useCoachSessionDetail } from './useCoachSessionDetail';
import { useSessionTaxonomy } from './useSessionTaxonomy';
import {
  OBJECTIVE_LABELS,
  OBJECTIVE_COLORS,
} from '../CoachSessionWeekView/useCoachSessionWeekView';
import styles from './CoachSessionDetail.module.scss';

const PROFILE_CHIP_COLORS: Record<string, { bg: string; color: string }> = {
  PHYSICAL_DOMINANT: { bg: 'rgba(245, 179, 80, 0.15)', color: '#c47d00' },
  COGNITIVE_DOMINANT: { bg: 'rgba(6, 52, 68, 0.12)', color: '#063444' },
  BALANCED: { bg: 'rgba(77, 210, 146, 0.15)', color: '#1a8a5a' },
};

const PROFILE_LABEL: Record<string, string> = {
  PHYSICAL_DOMINANT: 'Fisico dom.',
  COGNITIVE_DOMINANT: 'Cognitivo dom.',
  BALANCED: 'Bilanciato',
};

/**
 * Pagina dettaglio / Session Builder.
 * Layout 70/30: sinistra canvas blocchi, destra pannello Roster/Analisi.
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md §3.
 * Route: /dashboard/teams/:teamId/sessions/:sessionId/edit
 */
const CoachSessionDetailPage: FC = () => {
  const { teamId, sessionId } = useParams<{
    teamId: string;
    sessionId: string;
  }>();
  const navigate = useNavigate();
  const { isTabVisible } = useModuleVisibility();

  const hook = useCoachSessionDetail(sessionId ?? '');
  const { sport, taxonomy } = useSessionTaxonomy(teamId);
  const {
    session,
    isLoading,
    localTitle,
    setLocalTitle,
    localBlocks,
    localObjective,
    setLocalObjective,
    localDate,
    setLocalDate,
    localStartTime,
    setLocalStartTime,
    panelTab,
    setPanelTab,
    saveDraft,
    publishMut,
    isSaving,
    addBlock,
    updateBlock,
    removeBlock,
    duplicateBlock,
    moveBlock,
  } = hook;

  // ─── Loading ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={styles.pageWrapper}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LeopartLoader size="md" />
        </div>
      </div>
    );
  }

  if (!session) return null;

  const m = session.metrics;
  const profileStyle = m?.loadProfile
    ? PROFILE_CHIP_COLORS[m.loadProfile]
    : undefined;

  const objColor = OBJECTIVE_COLORS[localObjective] ?? '#b5b5b5';
  const objLabel = OBJECTIVE_LABELS[localObjective] ?? localObjective;

  return (
    <div className={styles.pageWrapper}>
      {/* ─── Sticky header ──────────────────────────────────────── */}
      <div className={styles.stickyHeader}>
        <div className={styles.headerTop}>
          <div className={styles.headerLeft}>
            <button
              type="button"
              className={styles.backBtn}
              onClick={() => navigate(-1)}
              title="Torna alla lista"
            >
              <ArrowLeft size={18} />
            </button>
            <div className={styles.breadcrumb}>
              <span>Programmazione Campo</span>
              <span className={styles.breadcrumbSep}>›</span>
            </div>
            <input
              className={styles.titleInput}
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              placeholder="Titolo sessione…"
            />
            <span className={`${styles.statusPill} ${styles[session.status]}`}>
              {session.status === 'draft'
                ? 'Bozza'
                : session.status === 'published'
                ? 'Pubblicata'
                : 'Eseguita'}
            </span>
          </div>

          <div className={styles.headerRight}>
            {isSaving && (
              <span className={styles.autosaveIndicator}>
                <span className={`${styles.autosaveDot} ${styles.saving}`} />
                Salvataggio…
              </span>
            )}
            {!isSaving && session.status === 'draft' && (
              <span className={styles.autosaveIndicator}>
                <span className={styles.autosaveDot} />
                Auto-save attivo
              </span>
            )}
            <CustomButton
              color="outline"
              size="sm"
              onClick={saveDraft}
              isLoading={isSaving}
            >
              Salva bozza
            </CustomButton>
            {session.status === 'draft' && (
              <CustomButton
                color="secondary"
                size="sm"
                isLoading={publishMut.isPending}
                onClick={() => publishMut.mutate()}
              >
                Pubblica
              </CustomButton>
            )}
          </div>
        </div>

        {/* ─── Session info mini-form ─────────────────────── */}
        <div className={styles.sessionInfoCard} style={{ marginBottom: 0, border: 'none', padding: '0 0 8px', background: 'transparent' }}>
          <div className={styles.infoRow}>
            <div className={styles.infoField}>
              <label className={styles.infoLabel}>Data</label>
              <input
                className={styles.infoInput}
                type="date"
                value={localDate}
                onChange={(e) => setLocalDate(e.target.value)}
              />
            </div>
            <div className={styles.infoField}>
              <label className={styles.infoLabel}>Ora inizio</label>
              <input
                className={styles.infoInput}
                type="time"
                value={localStartTime}
                onChange={(e) => setLocalStartTime(e.target.value)}
              />
            </div>
            <div className={styles.infoField}>
              <label className={styles.infoLabel}>Obiettivo</label>
              <select
                className={styles.infoSelect}
                value={localObjective}
                onChange={(e) => setLocalObjective(e.target.value)}
              >
                <option value="">—</option>
                {Object.entries(OBJECTIVE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ─── Metrics strip ─────────────────────────────── */}
        {m && (
          <div className={styles.metricsStrip}>
            {m.totalDurationMinutes != null && (
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>Durata</span>
                <span className={styles.metricValue}>{m.totalDurationMinutes}'</span>
              </div>
            )}
            {m.ewtMinutes != null && (
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>EWT</span>
                <span className={styles.metricValue}>
                  {m.ewtMinutes}'
                  {m.ewtPct != null && (
                    <span style={{ color: '#888', fontWeight: 400 }}>
                      {' '}({m.ewtPct.toFixed(0)}%)
                    </span>
                  )}
                </span>
              </div>
            )}
            {m.avgIPS != null && (
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>IPS</span>
                <span className={styles.metricValue}>
                  {m.avgIPS.toFixed(1)}
                  {m.peakIPS != null && (
                    <span style={{ color: '#888', fontWeight: 400 }}>
                      {' '}(picco {m.peakIPS.toFixed(1)})
                    </span>
                  )}
                </span>
              </div>
            )}
            {m.avgCLS != null && (
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>CLS</span>
                <span className={styles.metricValue}>
                  {m.avgCLS.toFixed(1)}
                  {m.peakCLS != null && (
                    <span style={{ color: '#888', fontWeight: 400 }}>
                      {' '}(picco {m.peakCLS.toFixed(1)})
                    </span>
                  )}
                </span>
              </div>
            )}
            {m.totalCE != null && (
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>CE</span>
                <span className={styles.metricValue}>{m.totalCE.toFixed(1)}</span>
              </div>
            )}
            {m.estimatedAU != null && (
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>AU stim.</span>
                <span className={styles.metricValue}>{m.estimatedAU.toFixed(0)}</span>
              </div>
            )}
            {m.loadProfile && (
              <div className={styles.metricItem}>
                <span className={styles.metricLabel}>Profilo</span>
                <span
                  className={styles.profileChip}
                  style={{
                    background: profileStyle?.bg,
                    color: profileStyle?.color,
                    padding: '1px 7px',
                    borderRadius: 100,
                    fontSize: 10,
                    fontFamily: 'Satoshi, sans-serif',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  }}
                >
                  {PROFILE_LABEL[m.loadProfile] ?? m.loadProfile}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Content 70/30 ──────────────────────────────────────── */}
      <div className={styles.contentRow}>
        {/* Canvas 70% */}
        <div className={styles.canvasCol}>
          <RecoveryAlertsBanner
            teamId={teamId}
            affectedSessionId={session._id}
          />
          <SessionCanvas
            blocks={localBlocks}
            onUpdate={updateBlock}
            onDelete={removeBlock}
            onDuplicate={duplicateBlock}
            onMoveUp={(i) => moveBlock(i, 'up')}
            onMoveDown={(i) => moveBlock(i, 'down')}
            onAdd={addBlock}
            sport={sport}
            taxonomy={taxonomy}
            totalBlocks={localBlocks.length}
            rosterAthletes={session.athletes ?? []}
          />
        </div>

        {/* Right panel 30% */}
        <div className={styles.panelCol}>
          {/* Toggle — i tab con flag false vengono nascosti */}
          <div className={styles.panelToggle}>
            {isTabVisible('coach-session-detail-roster') && (
              <button
                type="button"
                className={`${styles.panelToggleBtn} ${
                  panelTab === 'roster' ? styles.active : ''
                }`}
                onClick={() => setPanelTab('roster')}
              >
                <UsersThree size={13} />
                Roster
              </button>
            )}
            {isTabVisible('coach-session-detail-analysis') && (
              <button
                type="button"
                className={`${styles.panelToggleBtn} ${
                  panelTab === 'analysis' ? styles.active : ''
                }`}
                onClick={() => setPanelTab('analysis')}
              >
                <ChartBar size={13} />
                Analisi
              </button>
            )}
          </div>

          <div className={styles.panelScroll}>
            {panelTab === 'roster' && (
              <RosterPanel athletes={session.athletes ?? []} sport={sport} />
            )}
            {panelTab === 'analysis' && (
              <AnalysisPanel metrics={session.metrics} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachSessionDetailPage;
